import { useGlobalState, useResponsive } from '@/hooks';
import { ResponsiveCollection } from '@/interfaces';
import { Skeleton } from '@/modules/pmt/components/Skeleton';
import { usePopUpsContext, useShipmentDetailsContext } from '@/modules/pmt/context';
import type { IRatingModuleProps } from '@/modules/pmt/interfaces';
import { resetDataLayerAndPushEvent } from '@/modules/pmt/utils';
import { init, loadRemote } from '@module-federation/enhanced/runtime';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { type ComponentType, Suspense, lazy, memo, useMemo } from 'react';

const isDevelopment = process.env.NODE_ENV === 'development';
const { publicRuntimeConfig } = getConfig();
const remoteEntryHost = isDevelopment ? 'http://localhost:3002' : publicRuntimeConfig.feRatingUrl;

init({
  name: 'pmc',
  remotes: [
    {
      name: 'rating_mfe',
      entry: `${remoteEntryHost}/rating_mfe.js`,
    },
  ],
});

const Component = lazy(
  async () => loadRemote('rating_mfe/rating') as Promise<{ default: ComponentType<IRatingModuleProps> }>,
);

const RatingRemoteModule = memo(() => {
  const { query } = useRouter();
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);
  const { t } = useGlobalState();
  const { currentShipment, setQueryParamsToRemove } = useShipmentDetailsContext();
  const { pauseRunningPopUps } = usePopUpsContext();

  const skeletonHeight = isDesktopPMT ? 110 : 88;

  const removeQueryParams = (keys: string[]) => {
    setQueryParamsToRemove(keys);
  };

  const pageConfigs = useMemo(
    () => ({
      isDesktop: isDesktopPMT,
      translate: t,
      resetDataLayerAndPushEvent,
      removeQueryParams,
      query,
      pauseRunningPopUps,
      currentShipmentData: currentShipment?.data,
    }),
    [isDesktopPMT, t, currentShipment, query],
  );

  if (!currentShipment?.data.shipmentPk) return null;

  return (
    <Suspense fallback={<Skeleton height={skeletonHeight} />}>
      <Component
        teSlug={publicRuntimeConfig.teSlug}
        shipmentId={currentShipment.data.shipmentPk}
        pageConfigs={pageConfigs}
      />
    </Suspense>
  );
});

const RatingSection = () => {
  return <RatingRemoteModule />;
};

export { RatingSection };
